import {
    createTheme,
    getActionPalette,
    getInterfacePalette,
    mergeComponentOverrides,
    type ThemeMode,
    withTypographyTextColors,
} from 'src/lib/style/theme';
import { baseTheme } from 'src/theme';
import { RESQ_BRAND_COLORS } from 'src/constants/palette';

/**
 * Build vendor-specific theme with light or dark mode support.
 * @param mode - 'light' or 'dark'
 * @returns Theme object with vendor customizations
 */
export const buildVendorTheme = (mode: ThemeMode = 'light') => {
    const isDark = mode === 'dark';
    const actionPalette = getActionPalette(mode);
    const interfacePalette = getInterfacePalette(mode);

    const vendorTheme = createTheme({
        ...baseTheme,
        palette: {
            ...baseTheme.palette,
            success: {
                main: interfacePalette.surface.success,
                contrastText: interfacePalette.text.onSuccess,
            },
            error: {
                main: interfacePalette.surface.critical,
                contrastText: interfacePalette.text.onCritical,
            },
            warning: {
                main: interfacePalette.surface.warning,
                contrastText: interfacePalette.text.onWarning,
            },
            info: { main: interfacePalette.text.link },
            divider: interfacePalette.divider.default,
            mode, // Set MUI palette mode
            primary: actionPalette.primary.mui,
            secondary: actionPalette.secondary.mui,
            text: {
                primary: interfacePalette.text.default,
                secondary: interfacePalette.text.subdued,
            },
            background: {
                default: interfacePalette.background.default,
                paper: interfacePalette.surface.default,
            },
        },
        typography: withTypographyTextColors(baseTheme.typography, interfacePalette.text),
        resqPalette: {
            toggle: interfacePalette.toggle,
            background: {
                ...interfacePalette.background,
            },
            surface: {
                ...interfacePalette.surface,
                // Critical surfaces
                // Same red in both modes so white labels reach 4.5:1.
                // Success surfaces
                // Highlight surfaces
                highlight: '#7B5DE6',
                highlight2: isDark ? '#E0E0E0' : RESQ_BRAND_COLORS.deepTeal,
                highlightSubdued: isDark ? '#2D2440' : '#F2EFFC',
                // Warning surfaces
                // Decorative surfaces (vendor-specific)
                decorative1: '#7D60E6',
                decorative1Subdued: isDark ? '#2D2440' : '#F2EFFC',
                decorative2: '#FF4801',
                decorative2Subdued: isDark ? '#3D2518' : '#FDEEE5',
                decorative3: isDark ? '#1A4D4E' : '#00A1A3',
                decorative3Subdued: isDark ? '#1A3334' : '#DBF6F6',
                decorative4: '#AB8D40',
                decorative4Subdued: isDark ? '#3D3420' : '#FCF0D0',
                // Nora brand surfaces
                nora: '#8FA88B',
                noraDark: '#2F3B34',
                noraGradient:
                    'conic-gradient(from 124deg at 57.19% 134.3%, #6F935E 0deg, #485B40 360deg)',
                noraFooter: '#2A3A49',
                noraDivider: RESQ_BRAND_COLORS.deepTeal,
            },
            text: {
                ...interfacePalette.text,

                // Critical text
                // Success text
                // Highlight text
                highlight: isDark ? '#B1A0F0' : '#6248C5',
                onHighlight: '#FFFFFF',
                // Warning text
                // Decorative text (vendor-specific)
                decorative1: isDark ? '#B1A0F0' : '#7556E4',
                decorative2: isDark ? '#F4935D' : '#994613',
                decorative3: isDark ? '#4DD4D6' : '#00696B',
                decorative4: isDark ? '#CAA74B' : '#705C29',
                onDecorative2Subdued: isDark ? '#F4935D' : '#994613',
            },
            icon: {
                ...interfacePalette.icon,

                // Warning icons
                // Critical icons
                // Success icons
                // Highlight icons
                highlight: '#7D60E6',
                onHighlight: '#FFFFFF',
                // Decorative icons (vendor-specific)
                decorative1: '#7D60E6',
                decorative1Subdued: isDark ? '#B1A0F0' : '#F4935D',
                decorative2: '#FF4801',
                decorative2Subdued: isDark ? '#F4935D' : '#F4935D',
                decorative3: isDark ? '#4DD4D6' : '#00A1A3',
                decorative3Subdued: isDark ? '#4DD4D6' : '#00BEC0',
                decorative4: '#AB8D40',
                decorative4Subdued: '#CAA74B',
                decorative4Bright: '#F6D06E',
            },
            border: {
                ...interfacePalette.border,

                onPrimary: isDark ? 'rgba(245, 242, 236, 0.3)' : 'rgba(11, 31, 58, 0.3)',

                onSecondary: isDark ? 'rgba(245, 242, 236, 0.3)' : 'rgba(11, 31, 58, 0.3)',
                // Critical borders
                // Success borders
                // Highlight borders
                highlight: '#7D60E6',
                highlightSubdued: '#9780EB',
                // Warning borders
                // Decorative borders (vendor-specific)
                decorative1: isDark ? '#9780EB' : '#7D60E6',
                decorative2: '#FF4801',
                decorative3: isDark ? '#4DD4D6' : '#00A1A3',
                decorative4: '#AB8D40',
            },
            divider: {
                ...interfacePalette.divider,
            },
        },
    });

    // Base overrides already resolve per theme; these layer the vendor's own look on top.
    vendorTheme.components = mergeComponentOverrides(vendorTheme.components, {
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    borderRadius: vendorTheme.spacing(1),
                    backgroundColor: vendorTheme.resqPalette.surface.primary,
                },
                root: {
                    backgroundColor: 'transparent',
                    borderRadius: 0,
                    padding: 0,
                    border: 'none',
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    backgroundColor: vendorTheme.resqPalette.surface.default,
                    color: vendorTheme.resqPalette.text.default,
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.default,
                    '&:hover': {
                        backgroundColor: vendorTheme.resqPalette.surface.hovered,
                    },
                    '&.Mui-selected': {
                        backgroundColor: vendorTheme.resqPalette.surface.selected,
                        '&:hover': {
                            backgroundColor: vendorTheme.resqPalette.surface.hovered,
                        },
                    },
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    color: vendorTheme.resqPalette.text.default,
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.default,
                },
            },
        },
        MuiSvgIcon: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.icon.default,
                    '.MuiIconButton-root &, .MuiButton-root &': {
                        color: 'inherit',
                    },
                    '.MuiIconButton-root.Mui-disabled &': {
                        color: vendorTheme.resqPalette.icon.disabled,
                    },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.icon.default,
                    '&.Mui-disabled': {
                        color: vendorTheme.resqPalette.icon.disabled,
                    },
                },
            },
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.icon.subdued,
                    '& .MuiSvgIcon-root': {
                        color: vendorTheme.resqPalette.icon.subdued,
                    },
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.icon.default,
                    minWidth: '40px',
                },
            },
        },
        MuiListItemText: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.default,
                },
                primary: {
                    color: vendorTheme.resqPalette.text.default,
                },
                secondary: {
                    color: vendorTheme.resqPalette.text.subdued,
                },
            },
        },
        MuiList: {
            styleOverrides: {
                root: {
                    backgroundColor: vendorTheme.resqPalette.surface.default,
                    color: vendorTheme.resqPalette.text.default,
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.default,
                    '&:hover': {
                        backgroundColor: vendorTheme.resqPalette.surface.hovered,
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    '&:hover': {
                        backgroundColor: vendorTheme.resqPalette.surface.hovered,
                    },
                },
                body: {
                    color: vendorTheme.resqPalette.text.default,
                },
                footer: {
                    color: vendorTheme.resqPalette.text.default,
                },
            },
        },
        MuiTableSortLabel: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.subdued,
                    '&:hover': {
                        color: vendorTheme.resqPalette.text.default,
                    },
                    '&.Mui-active': {
                        color: vendorTheme.resqPalette.text.default,
                        '& .MuiTableSortLabel-icon': {
                            color: vendorTheme.resqPalette.icon.default,
                        },
                    },
                },
                icon: {
                    color: vendorTheme.resqPalette.icon.subdued,
                },
            },
        },
        MuiTablePagination: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.default,
                },
                select: {
                    color: vendorTheme.resqPalette.text.default,
                },
                selectIcon: {
                    color: vendorTheme.resqPalette.icon.default,
                },
                actions: {
                    color: vendorTheme.resqPalette.icon.default,
                    '& .MuiIconButton-root': {
                        color: `${vendorTheme.resqPalette.icon.default} !important`,
                        '&.Mui-disabled': {
                            color: `${vendorTheme.resqPalette.icon.disabled} !important`,
                        },
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundColor: vendorTheme.resqPalette.surface.default,
                    color: vendorTheme.resqPalette.text.default,
                    boxShadow: 'none',
                },
            },
        },
        MuiTypography: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.default,
                    '&.MuiTypography-body3': {
                        color: vendorTheme.resqPalette.text.subdued,
                    },
                },
            },
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    backgroundColor: vendorTheme.resqPalette.surface.default,
                    color: vendorTheme.resqPalette.text.default,
                    '&:before': {
                        backgroundColor: vendorTheme.resqPalette.divider.default,
                    },
                },
            },
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.default,
                    '& .MuiAccordionSummary-expandIconWrapper': {
                        color: vendorTheme.resqPalette.icon.default,
                    },
                },
                expandIconWrapper: {
                    color: vendorTheme.resqPalette.icon.default,
                },
            },
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.default,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundColor: vendorTheme.resqPalette.surface.default,
                    color: vendorTheme.resqPalette.text.default,
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: vendorTheme.resqPalette.background.default,
                    color: vendorTheme.resqPalette.text.default,
                    // React Big Calendar overrides for dark mode
                    '& .rbc-today': {
                        backgroundColor: vendorTheme.resqPalette.surface.highlightSubdued,
                    },
                    '& .rbc-selected-cell': {
                        backgroundColor: `${vendorTheme.resqPalette.surface.selected} !important`,
                    },
                    '& .rbc-now': {
                        color: vendorTheme.resqPalette.text.default,
                    },
                    '& .rbc-current': {
                        color: vendorTheme.resqPalette.text.default,
                    },
                    '& .rbc-off-range-bg': {
                        backgroundColor: vendorTheme.resqPalette.surface.subdued,
                    },
                    '& .rbc-header': {
                        color: vendorTheme.resqPalette.text.default,
                        borderColor: vendorTheme.resqPalette.divider.default,
                    },
                    '& .rbc-date-cell': {
                        color: vendorTheme.resqPalette.text.default,
                    },
                    '& .rbc-month-view': {
                        backgroundColor: vendorTheme.resqPalette.surface.default,
                        borderColor: vendorTheme.resqPalette.divider.default,
                    },
                    '& .rbc-day-bg': {
                        borderColor: vendorTheme.resqPalette.divider.default,
                    },
                    '& .rbc-month-row': {
                        borderColor: vendorTheme.resqPalette.divider.default,
                    },
                },
            },
        },
    });

    return vendorTheme;
};

/**
 * @deprecated Use buildVendorTheme('light') instead. This static export will be removed in Phase 2.
 */
export const vendorTheme = buildVendorTheme('light');
